// Settings › Apps and keys (plan step 2.2; D6, D67, D71; Console D, APS-319): the project's apps,
// one per platform, their keys (rotate, revoke) in one table, adding an app, and the start call
// with the app's own key. Admin actions answer 403 for members; the page says so instead of
// hiding them.
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { isApiError } from '../api/client'
import { appsQuery, projectsQuery, useCreateApp, useRevokeKey, useRotateKey } from '../api/queries'
import type { App, Platform } from '../api/types'
import { ActionError } from '../components/ActionError'
import { Confirm } from '../components/Confirm'
import {
  type CodeTab,
  CodeTabs,
  copyText,
  docTabs,
  EmptyNote,
  PageHead,
  ScrollX,
  Section,
  Status,
  useToast,
} from '../components/d'
import { Icon } from '../components/icons'
import { appLabel, PLATFORM_LABEL } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { ingestHost } from '../lib/conventions'
import { DOCS, type DocSnippet, KEY_PLACEHOLDER, ownStartTabs } from '../lib/docSnippets'
import { day } from '../lib/format'
import { activeKey, StartNote } from './Setup'

const ADD_APP = 'se-add-app'

/**
 * A key or an id with its copy button (the mock's `.se-key`); never translated. `shown` is a
 * shortened form for a narrow cell, the whole value its title.
 */
export function KeyCell({
  value,
  shown,
  onCopied,
}: {
  value: string
  shown?: string
  onCopied: (ok: boolean) => void
}) {
  const { t } = useI18n()
  return (
    <span className="se-key">
      <code translate="no" title={shown ? value : undefined}>
        {shown ?? value}
      </code>
      <button
        type="button"
        aria-label={t('settings.keys.copy', { key: value })}
        onClick={async () => onCopied(await copyText(value))}
      >
        <Icon name="copy" className="se-i" />
      </button>
    </span>
  )
}

const PLATFORMS: readonly Platform[] = ['ios', 'android', 'react_native']

/**
 * The start code of each platform's SDK, from the docs: once per app with its own active key
 * (named by the app when the platform has several), or with the docs' placeholder key when the
 * platform has no app with a key yet. `note` gets the docs snippet, the app and the platform.
 */
export function installTabs(
  apps: readonly App[],
  note: (snippet: DocSnippet, app: App | undefined, platform: Platform) => ReactNode,
): CodeTab[] {
  return PLATFORMS.flatMap((platform) => {
    const keyed = apps.flatMap((app) => {
      const key = app.platform === platform ? activeKey(app) : undefined
      return key ? [{ app, key }] : []
    })
    const rows = keyed.length ? keyed : [{ app: undefined, key: KEY_PLACEHOLDER }]
    return rows.flatMap(({ app, key }) =>
      docTabs(ownStartTabs(platform, key), (x) => note(x, app, platform)).map((tab) => {
        const own =
          tab.label === PLATFORM_LABEL[platform]
            ? tab.label
            : `${PLATFORM_LABEL[platform]} · ${tab.label}`
        return {
          ...tab,
          id: `${app?.id ?? platform}-${tab.id}`,
          label: keyed.length > 1 && app ? `${app.name} · ${own}` : own,
        }
      }),
    )
  })
}

/** Every key of every app, newest first, with its app. */
export function keyRows(apps: readonly App[]) {
  return apps
    .flatMap((app) => app.keys.map((k) => ({ app, key: k })))
    .sort((a, b) => b.key.created_at.localeCompare(a.key.created_at))
}

function AddApp({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useI18n()
  const create = useCreateApp(orgId, projectId)
  const [platform, setPlatform] = useState<Platform>('ios')
  const [name, setName] = useState('')
  const [bundle, setBundle] = useState('')
  const hint = useId()
  function submit(e: FormEvent) {
    e.preventDefault()
    create.mutate(
      { platform, name: name.trim(), bundle_id: bundle.trim() },
      {
        onSuccess: () => {
          setName('')
          setBundle('')
        },
      },
    )
  }
  return (
    <div className="win">
      <div className="win-bar">
        <b>{t('settings.apps.add')}</b>
        <span>{t('settings.apps.addSub')}</span>
      </div>
      <form className="se-form" onSubmit={submit} aria-label={t('settings.apps.add')}>
        <label className="lbl">
          {t('firstRun.platformLabel')}
          <select
            className="select"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform)}
          >
            {(Object.keys(PLATFORM_LABEL) as Platform[]).map((p) => (
              <option key={p} value={p}>
                {PLATFORM_LABEL[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="lbl">
          {t('firstRun.appName')}
          <input
            className="input"
            required
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {/* The hint outside the label: the field's name stays its label alone. */}
        <div style={{ display: 'grid', gap: 6 }}>
          <label className="lbl">
            {t('firstRun.bundleId')}
            <input
              className="input mono"
              required
              maxLength={255}
              pattern="[A-Za-z0-9._\-]+"
              placeholder="com.example.app"
              translate="no"
              aria-describedby={hint}
              value={bundle}
              onChange={(e) => setBundle(e.target.value)}
            />
          </label>
          <span className="se-hint" id={hint}>
            {t('firstRun.bundleHint')}
          </span>
        </div>
        {create.error ? (
          <p role="alert" className="notice error">
            {isApiError(create.error, 409)
              ? t('firstRun.appExists')
              : isApiError(create.error, 403)
                ? t('settings.adminOnly')
                : t('state.failed')}
          </p>
        ) : null}
        {create.data ? (
          <p role="status" className="notice ok">
            {t('settings.apps.added', { app: appLabel(create.data.app) })}{' '}
            <span className="mono" translate="no">
              {create.data.key}
            </span>
          </p>
        ) : null}
        <div className="se-row">
          <button type="submit" className="btn solid" disabled={create.isPending}>
            {t('firstRun.createApp')}
          </button>
        </div>
      </form>
    </div>
  )
}

function Rotate({
  apps,
  orgId,
  projectId,
}: {
  apps: readonly App[]
  orgId: string
  projectId: string
}) {
  const { t } = useI18n()
  const rotate = useRotateKey(orgId, projectId)
  const [appId, setAppId] = useState(apps[0]?.id ?? '')
  const [revokePrevious, setRevokePrevious] = useState(false)
  return (
    <div className="se-sp" style={{ paddingTop: 12 }}>
      <p className="se-p">{t('settings.keys.rotateHelp')}</p>
      <div
        className="se-row"
        style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <select
          className="select"
          style={{ width: 'auto', maxWidth: '100%' }}
          aria-label={t('settings.keys.app')}
          value={appId}
          onChange={(e) => setAppId(e.target.value)}
        >
          {apps.map((a) => (
            <option key={a.id} value={a.id}>
              {appLabel(a)}
            </option>
          ))}
        </select>
        <label className="se-chk">
          <input
            type="checkbox"
            checked={revokePrevious}
            onChange={(e) => setRevokePrevious(e.target.checked)}
          />
          {t('settings.keys.revokePrevious')}
        </label>
        <button
          type="button"
          className="btn sm"
          disabled={rotate.isPending || !appId}
          onClick={() => rotate.mutate({ appId, revokePrevious })}
        >
          {t('settings.keys.rotate')}
        </button>
      </div>
      {rotate.data ? (
        <p role="status" className="notice ok">
          {t('settings.keys.rotated')}{' '}
          <span className="mono" translate="no">
            {rotate.data.key}
          </span>
        </p>
      ) : null}
      <ActionError error={rotate.error} />
    </div>
  )
}

export function AppsSettings({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const i18n = useI18n()
  const { t } = i18n
  const apps = useQuery(appsQuery(orgId, projectId))
  const projects = useQuery(projectsQuery(orgId))
  const revoke = useRevokeKey(orgId, projectId)
  const { toast, show } = useToast()
  const project = projects.data?.find((p) => p.id === projectId)
  const copied = (ok: boolean) => show(ok ? t('settings.keys.copied') : t('d.code.copyFailed'))
  const list = apps.data ?? []
  // A note only where there is something to add: no app yet, or what Android and React Native
  // need besides the code.
  const install = installTabs(list, (x, app, platform) =>
    !app || platform !== 'ios' ? (
      <>
        {app ? null : (
          <>{t('settings.apps.installNoApp', { platform: PLATFORM_LABEL[platform] })} </>
        )}
        <StartNote snippet={x} guide={false} />
      </>
    ) : null,
  )

  return (
    <main id="main" className="pg se-pg apps">
      <PageHead
        crumb={crumb}
        title={title}
        meta={
          project && apps.data ? (
            <>
              <span translate="no">{project.name}</span>
              {` · ${t('settings.apps.count', { count: apps.data.length })}`}
            </>
          ) : undefined
        }
        actions={
          <a
            className="btn"
            href={`#${ADD_APP}`}
            onClick={(e) => {
              e.preventDefault()
              const form = document.getElementById(ADD_APP)
              form?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              form?.querySelector<HTMLElement>('select, input')?.focus({ preventScroll: true })
            }}
          >
            <Icon name="plus" className="se-i" />
            {t('settings.apps.addAction')}
          </a>
        }
      />
      {apps.isPending ? (
        <Loading />
      ) : apps.isError ? (
        <ErrorState error={apps.error} retry={() => apps.refetch()} />
      ) : (
        <div className="se-main">
          <Section title={t('settings.apps.apps')} meta={t('settings.apps.addSub')}>
            {list.length === 0 ? (
              <EmptyNote title={t('settings.apps.none')}>{t('settings.apps.noneBody')}</EmptyNote>
            ) : (
              <ScrollX label={t('settings.apps.apps')}>
                <table className="t" aria-label={t('settings.apps.apps')}>
                  <thead>
                    <tr>
                      <th className="f" scope="col">
                        {t('settings.apps.platform')}
                      </th>
                      <th scope="col">{t('settings.apps.name')}</th>
                      <th scope="col">{t('settings.apps.bundle')}</th>
                      <th scope="col">{t('settings.apps.key')}</th>
                      <th scope="col">{t('settings.apps.ingest')}</th>
                      <th className="num last" scope="col">
                        {t('settings.keys.created')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((a) => {
                      const key = activeKey(a)
                      return (
                        <tr key={a.id}>
                          <td className="f">{PLATFORM_LABEL[a.platform]}</td>
                          <td translate="no">{a.name}</td>
                          <td>
                            <code translate="no">{a.bundle_id}</code>
                          </td>
                          <td>{key ? <KeyCell value={key} onCopied={copied} /> : '—'}</td>
                          <td>
                            {key ? <code translate="no">{`https://${ingestHost(key)}`}</code> : '—'}
                          </td>
                          <td className="num last">{day(i18n, a.created_at)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </ScrollX>
            )}
          </Section>
          <div className="grid2">
            {list.length === 0 ? (
              <div />
            ) : (
              <Section
                title={t('settings.keys.title')}
                meta={<span title={t('settings.keys.sendOnly')}>{t('settings.keys.sub')}</span>}
              >
                <ScrollX label={t('settings.keys.title')}>
                  <table className="t" aria-label={t('settings.keys.title')}>
                    <thead>
                      <tr>
                        <th className="f" scope="col">
                          {t('settings.keys.key')}
                        </th>
                        <th scope="col">{t('settings.keys.app')}</th>
                        <th scope="col">{t('settings.keys.created')}</th>
                        <th scope="col">{t('settings.keys.state')}</th>
                        <th className="last" scope="col">
                          <span className="visually-hidden">{t('settings.keys.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {keyRows(list).map(({ app, key: k }) => {
                        const lastActive = app.keys.filter((x) => !x.revoked_at).length === 1
                        return (
                          <tr key={k.key}>
                            <td className="f">
                              {k.revoked_at ? (
                                <code className="faint" translate="no">
                                  {k.key}
                                </code>
                              ) : (
                                <KeyCell value={k.key} onCopied={copied} />
                              )}
                            </td>
                            <td>{appLabel(app)}</td>
                            <td>{day(i18n, k.created_at)}</td>
                            <td>
                              {k.revoked_at ? (
                                <Status
                                  s="mute"
                                  label={t('settings.keys.revokedOn', {
                                    date: day(i18n, k.revoked_at),
                                  })}
                                />
                              ) : (
                                <Status s="res" label={t('settings.keys.active')} />
                              )}
                            </td>
                            <td className="last" style={{ textAlign: 'right' }}>
                              {!k.revoked_at ? (
                                <Confirm
                                  label={t('settings.keys.revoke')}
                                  buttonClass="btn ghost sm"
                                  title={t('settings.keys.revokeTitle')}
                                  body={
                                    lastActive
                                      ? t('settings.keys.revokeLast')
                                      : t('settings.keys.revokeBody')
                                  }
                                  confirm={t('settings.keys.revokeConfirm')}
                                  busy={revoke.isPending}
                                  onConfirm={() => revoke.mutate({ appId: app.id, key: k.key })}
                                />
                              ) : null}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </ScrollX>
                <ActionError error={revoke.error} />
                <Rotate apps={list} orgId={orgId} projectId={projectId} />
              </Section>
            )}
            <div className="sections" id={ADD_APP}>
              <AddApp orgId={orgId} projectId={projectId} />
            </div>
          </div>
          {install.length > 0 ? (
            <Section
              title={t('settings.apps.install')}
              meta={t('settings.apps.installSub')}
              right={
                <a className="ul" href={`${DOCS}/quickstart`} target="_blank" rel="noreferrer">
                  {t('setup.sdk.guide')}
                </a>
              }
            >
              <CodeTabs label={t('settings.apps.install')} tabs={install} />
            </Section>
          ) : null}
        </div>
      )}
      {toast}
    </main>
  )
}
